// Import auth types
import type {
  AuthSettingsRequest,
  AuthSettingsResponse,
  AuthTokenResource,
  ExtendedAuthTokenResponse,
  ExtendedRefreshTokenRequest,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  RevokeTokenRequest,
  TokenStatisticsResource,
  ValidateTokenRequest,
} from '@interfaces/auth/auth.types';
import { createSlice } from '@reduxjs/toolkit';
import type { AppDispatch, AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';

export interface AuthReducerState {
  // Authentication state
  loginStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  loginError: string | null;

  // 2FA state
  twoFactorAuthEnabled: boolean;
  twoFactorQrCode: string | null;
  twoFactorRecoveryCodes: string[];
  twoFactorSecret: string | null;
  twoFactorStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  twoFactorError: string | null;

  // Auth tokens management
  authTokens: Record<string, AuthTokenResource>;
  tokenStatistics: TokenStatisticsResource | null;
  tokenStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  tokenError: string | null;

  // Auth settings
  authSettings: AuthSettingsResponse | null;
  settingsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  settingsError: string | null;

  // Password reset
  forgotPasswordStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  forgotPasswordError: string | null;
  resetPasswordStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  resetPasswordError: string | null;

  // General auth state
  isInitialized: boolean;
}

const initialState: AuthReducerState = {
  loginStatus: 'idle',
  loginError: null,
  twoFactorAuthEnabled: false,
  twoFactorQrCode: null,
  twoFactorRecoveryCodes: [],
  twoFactorSecret: null,
  twoFactorStatus: 'idle',
  twoFactorError: null,
  authTokens: {},
  tokenStatistics: null,
  tokenStatus: 'idle',
  tokenError: null,
  authSettings: null,
  settingsStatus: 'idle',
  settingsError: null,
  forgotPasswordStatus: 'idle',
  forgotPasswordError: null,
  resetPasswordStatus: 'idle',
  resetPasswordError: null,
  isInitialized: false,
};

// Authentication endpoints
export const loginUser = thunkPost.post<LoginRequest>('auth', '/api/v1/login');

export const logoutUser = thunkPost.post('auth', '/api/v1/logout');

export const logoutEverywhere = thunkDelete.del('auth', '/api/v1/logout/everywhere');

export const registerUser = thunkPost.post<RegisterRequest>('auth', '/api/v1/register');

export const forgotPassword = thunkPost.post<ForgotPasswordRequest>('auth', '/api/v1/forgot-password');

export const resetPassword = thunkPost.post<ResetPasswordRequest>('auth', '/api/v1/reset-password');

// 2FA endpoints
export const getTwoFactorAuth = thunkGet.get<undefined>('auth', '/api/v1/2fa');

export const enableTwoFactorAuth = thunkPost.post('auth', '/api/v1/2fa');

export const disableTwoFactorAuth = thunkDelete.del('auth', '/api/v1/2fa');

export const confirmTwoFactorAuth = thunkPost.post<{ code: string }>('auth', '/api/v1/2fa/confirm');

export const challengeTwoFactorAuth = thunkPost.post<{ code?: string; recovery_code?: string }>(
  'auth',
  '/api/v1/2fa/challenge',
);

export const getTwoFactorQrCode = thunkGet.get<undefined>('auth', '/api/v1/2fa/qr-code');

export const generateTwoFactorRecoveryCodes = thunkPost.post('auth', '/api/v1/2fa/recovery-codes');

// Auth token management
export const getAuthTokens = thunkGet.get<undefined>('auth', '/api/v1/auth-tokens');

export const createAuthToken = thunkPost.post<{ name: string; abilities?: string[] }>('auth', '/api/v1/auth-tokens');

export const getAuthToken = thunkGet.get<{ tokenId: string }>('auth', '/api/v1/auth-tokens/{tokenId}');

export const deleteAuthToken = thunkDelete.del<{ tokenId: string }>('auth', '/api/v1/auth-tokens/{tokenId}');

export const getTokenStatistics = thunkGet.get<undefined>('auth', '/api/v1/auth-tokens/statistics');

export const validateAuthToken = thunkPost.post<{ token: string }>('auth', '/api/v1/auth-tokens/validate');

// Sanctum token endpoints
export const createSanctumToken = thunkPost.post<{ email: string; password: string; device_name: string }>(
  'auth',
  '/api/v1/sanctum/token',
);

export const refreshSanctumToken = thunkPost.post<{ refresh_token: string }>('auth', '/api/v1/sanctum/token/refresh');

export const revokeAllTokens = thunkDelete.del('auth', '/api/v1/tokens/all');

export const revokeCurrentToken = thunkDelete.del('auth', '/api/v1/tokens/current');

export const revokeRefreshToken = thunkDelete.del('auth', '/api/v1/tokens/refresh');

export const listUserTokens = thunkGet.get<undefined>('auth', '/api/v1/tokens');

// Extended token management endpoints
export const refreshAuthToken = thunkPost.post<ExtendedRefreshTokenRequest, ExtendedAuthTokenResponse>(
  'auth',
  '/api/v1/auth/refresh',
);

export const revokeAuthToken = thunkPost.post<RevokeTokenRequest>('auth', '/api/v1/auth/revoke');

export const validateTokenRequest = thunkPost.post<ValidateTokenRequest>('auth', '/api/v1/auth/validate');

export const getAuthTokenInfo = thunkGet.get<undefined>('auth', '/api/v1/auth/token-info');

// Auth settings management
export const getAuthSettings = thunkGet.get<undefined>('auth', '/api/v1/auth/settings');

export const updateAuthSettings = thunkPut.put<AuthSettingsRequest>('auth', '/api/v1/auth/settings');

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthState: (state) => {
      state.loginStatus = 'idle';
      state.loginError = null;
      state.twoFactorAuthEnabled = false;
      state.twoFactorQrCode = null;
      state.twoFactorRecoveryCodes = [];
      state.twoFactorSecret = null;
      state.twoFactorStatus = 'idle';
      state.twoFactorError = null;
      state.authTokens = {};
      state.tokenStatistics = null;
      state.tokenStatus = 'idle';
      state.tokenError = null;
      state.authSettings = null;
      state.settingsStatus = 'idle';
      state.settingsError = null;
      state.forgotPasswordStatus = 'idle';
      state.forgotPasswordError = null;
      state.resetPasswordStatus = 'idle';
      state.resetPasswordError = null;
      state.isInitialized = false;
    },
    markAuthInitialized: (state) => {
      state.isInitialized = true;
    },
    setTwoFactorEnabled: (state, action) => {
      state.twoFactorAuthEnabled = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout action
      .addCase(meLogOut.fulfilled, (state) => {
        state.loginStatus = 'idle';
        state.loginError = null;
        state.twoFactorAuthEnabled = false;
        state.twoFactorQrCode = null;
        state.twoFactorRecoveryCodes = [];
        state.twoFactorSecret = null;
        state.authTokens = {};
        state.tokenStatistics = null;
        state.isInitialized = false;
      })

      // Login
      .addCase(loginUser.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginError = null;
      })
      .addCase(loginUser.fulfilled, (state) => {
        state.loginStatus = 'succeeded';
        // Handle successful login
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loginError = action.error.message || 'Login failed';
      })

      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.loginStatus = 'idle';
        state.authTokens = {};
        state.tokenStatistics = null;
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginError = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loginStatus = 'succeeded';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loginError = action.error.message || 'Registration failed';
      })

      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordStatus = 'loading';
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordStatus = 'succeeded';
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordStatus = 'failed';
        state.forgotPasswordError = action.error.message || 'Failed to send reset email';
      })

      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordStatus = 'loading';
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordStatus = 'succeeded';
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordStatus = 'failed';
        state.resetPasswordError = action.error.message || 'Password reset failed';
      })

      // 2FA Management
      .addCase(getTwoFactorAuth.fulfilled, (state, action) => {
        state.twoFactorAuthEnabled = action.payload?.enabled || false;
      })
      .addCase(enableTwoFactorAuth.pending, (state) => {
        state.twoFactorStatus = 'loading';
        state.twoFactorError = null;
      })
      .addCase(enableTwoFactorAuth.fulfilled, (state, action) => {
        state.twoFactorStatus = 'succeeded';
        state.twoFactorAuthEnabled = true;
        if (action.payload?.secret) {
          state.twoFactorSecret = action.payload.secret;
        }
      })
      .addCase(enableTwoFactorAuth.rejected, (state, action) => {
        state.twoFactorStatus = 'failed';
        state.twoFactorError = action.error.message || 'Failed to enable 2FA';
      })
      .addCase(disableTwoFactorAuth.fulfilled, (state) => {
        state.twoFactorAuthEnabled = false;
        state.twoFactorQrCode = null;
        state.twoFactorRecoveryCodes = [];
        state.twoFactorSecret = null;
      })
      .addCase(confirmTwoFactorAuth.fulfilled, (state) => {
        state.twoFactorAuthEnabled = true;
        state.twoFactorStatus = 'succeeded';
      })
      .addCase(getTwoFactorQrCode.fulfilled, (state, action) => {
        state.twoFactorQrCode = action.payload?.qrCode || null;
      })
      .addCase(generateTwoFactorRecoveryCodes.fulfilled, (state, action) => {
        if (Array.isArray(action.payload?.recoveryCodes)) {
          state.twoFactorRecoveryCodes = action.payload.recoveryCodes;
        }
      })

      // Auth Token Management
      .addCase(getAuthTokens.pending, (state) => {
        state.tokenStatus = 'loading';
        state.tokenError = null;
      })
      .addCase(getAuthTokens.fulfilled, (state, action) => {
        state.tokenStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          state.authTokens = {};
          action.payload.forEach((token: AuthTokenResource) => {
            state.authTokens[token.id] = token;
          });
        }
      })
      .addCase(getAuthTokens.rejected, (state, action) => {
        state.tokenStatus = 'failed';
        state.tokenError = action.error.message || 'Failed to load tokens';
      })
      .addCase(createAuthToken.fulfilled, (state, action) => {
        if (action.payload?.id) {
          state.authTokens[action.payload.id] = action.payload;
        }
      })
      .addCase(deleteAuthToken.fulfilled, (state, action) => {
        const tokenId = action.meta.arg?.urlVarsReplaceMap?.tokenId;
        if (tokenId) {
          delete state.authTokens[tokenId];
        }
      })
      .addCase(getTokenStatistics.fulfilled, (state, action) => {
        state.tokenStatistics = action.payload;
      })

      // Extended token management
      .addCase(refreshAuthToken.pending, (state) => {
        state.tokenStatus = 'loading';
        state.tokenError = null;
      })
      .addCase(refreshAuthToken.fulfilled, (state) => {
        state.tokenStatus = 'succeeded';
        // Token refresh handled by auth system
      })
      .addCase(refreshAuthToken.rejected, (state, action) => {
        state.tokenStatus = 'failed';
        state.tokenError = action.error.message || 'Token refresh failed';
      })
      .addCase(revokeAuthToken.fulfilled, (state) => {
        // Token revoked successfully
        state.tokenStatus = 'succeeded';
      })
      .addCase(validateTokenRequest.fulfilled, (state) => {
        // Token validation completed
        state.tokenStatus = 'succeeded';
      })

      // Auth settings management
      .addCase(getAuthSettings.pending, (state) => {
        state.settingsStatus = 'loading';
        state.settingsError = null;
      })
      .addCase(getAuthSettings.fulfilled, (state, action) => {
        state.settingsStatus = 'succeeded';
        state.authSettings = action.payload;
      })
      .addCase(getAuthSettings.rejected, (state, action) => {
        state.settingsStatus = 'failed';
        state.settingsError = action.error.message || 'Failed to load auth settings';
      })
      .addCase(updateAuthSettings.pending, (state) => {
        state.settingsStatus = 'loading';
        state.settingsError = null;
      })
      .addCase(updateAuthSettings.fulfilled, (state, action) => {
        state.settingsStatus = 'succeeded';
        state.authSettings = action.payload;
      })
      .addCase(updateAuthSettings.rejected, (state, action) => {
        state.settingsStatus = 'failed';
        state.settingsError = action.error.message || 'Failed to update auth settings';
      });
  },
});

export const { clearAuthState, markAuthInitialized, setTwoFactorEnabled } = authSlice.actions;

// Selectors
export const selectLoginStatus = (state: AppState) => state.auth.loginStatus;
export const selectLoginError = (state: AppState) => state.auth.loginError;

export const selectTwoFactorAuthEnabled = (state: AppState) => state.auth.twoFactorAuthEnabled;
export const selectTwoFactorQrCode = (state: AppState) => state.auth.twoFactorQrCode;
export const selectTwoFactorRecoveryCodes = (state: AppState) => state.auth.twoFactorRecoveryCodes;
export const selectTwoFactorSecret = (state: AppState) => state.auth.twoFactorSecret;
export const selectTwoFactorStatus = (state: AppState) => state.auth.twoFactorStatus;
export const selectTwoFactorError = (state: AppState) => state.auth.twoFactorError;

export const selectAuthTokens = (state: AppState) => state.auth.authTokens;
export const selectTokenStatistics = (state: AppState) => state.auth.tokenStatistics;
export const selectTokenStatus = (state: AppState) => state.auth.tokenStatus;
export const selectTokenError = (state: AppState) => state.auth.tokenError;

export const selectAuthSettings = (state: AppState) => state.auth.authSettings;
export const selectSettingsStatus = (state: AppState) => state.auth.settingsStatus;
export const selectSettingsError = (state: AppState) => state.auth.settingsError;

export const selectForgotPasswordStatus = (state: AppState) => state.auth.forgotPasswordStatus;
export const selectForgotPasswordError = (state: AppState) => state.auth.forgotPasswordError;
export const selectResetPasswordStatus = (state: AppState) => state.auth.resetPasswordStatus;
export const selectResetPasswordError = (state: AppState) => state.auth.resetPasswordError;

export const selectIsAuthInitialized = (state: AppState) => state.auth.isInitialized;

// Helper selectors
export const selectAuthTokenById = (tokenId: string) => (state: AppState) => state.auth.authTokens[tokenId];

export const selectActiveAuthTokens = (state: AppState) =>
  Object.values(state.auth.authTokens).filter((token: AuthTokenResource) => token.isActive);

export const selectExpiredAuthTokens = (state: AppState) =>
  Object.values(state.auth.authTokens).filter(
    (token: AuthTokenResource) => token.expiresAt && new Date(token.expiresAt) < new Date(),
  );

// Load initial auth data action
export const loadInitialAuthData = () => (dispatch: AppDispatch, getState: () => AppState) => {
  const state = getState();

  // Only load if user is authenticated
  if (state.me?.user) {
    dispatch(getTwoFactorAuth());
    dispatch(getAuthTokens());
    dispatch(getTokenStatistics());
    dispatch(getAuthSettings());
    dispatch(markAuthInitialized());
  }
};

export default authSlice.reducer;
